/**
 * Consultant Feedback Form types.
 *
 * Captures in-app feedback from consultants during the CRM audit window so we
 * have first-hand pain points to triage against. See `docs/audit/consultant-crm-audit-plan.md`.
 *
 * Submissions are read by jacos@propinvest.co.za (via email on every submission
 * — see functions/src/sendFeedbackEmail.ts) and stored in the
 * `consultantFeedback` Firestore collection for the audit catalog.
 */

import { Timestamp } from 'firebase/firestore';

export type FeedbackCategory =
  | 'bug'              // something is broken
  | 'missing_feature'  // I wish the system did X
  | 'confusing_ux'     // the UI works but is confusing
  | 'other';

export type FeedbackSeverity = 'low' | 'medium' | 'high' | 'blocker';

export type FeedbackStatus = 'new' | 'triaged' | 'fixed' | 'wont_fix' | 'duplicate';

export interface FeedbackSubmission {
  // Who
  consultantId: string;          // users doc id
  consultantAuthUid: string;     // Firebase Auth UID (for dual-ID correlation)
  consultantName: string;
  consultantEmail: string;
  consultantRole: string;

  // What
  whereInSystem: string;         // free text — page or feature name
  category: FeedbackCategory;
  severity: FeedbackSeverity;
  description: string;

  // Context (auto-captured)
  submittedFromUrl: string;      // window.location.href at submission time
  userAgent: string;

  // Optional screenshot — Firebase Storage download URL. Saved separately
  // because it requires a Storage upload BEFORE the Firestore write.
  screenshotUrl?: string;
  screenshotPath?: string;       // Storage path (for admin cleanup if ever needed)

  // Lifecycle
  status: FeedbackStatus;
  createdAt: Timestamp;
  triagedAt?: Timestamp;
  resolvedAt?: Timestamp;
  resolution?: string;
  emailSent: boolean;            // set by sendFeedbackEmail trigger
  emailSentAt?: Timestamp;
}

export interface FeedbackFormData {
  whereInSystem: string;
  category: FeedbackCategory;
  severity: FeedbackSeverity;
  description: string;
  screenshot?: File | null;
}

export const FEEDBACK_CATEGORY_LABELS: Record<FeedbackCategory, string> = {
  bug: 'Bug — something is broken',
  missing_feature: 'Missing feature — I need the system to do X',
  confusing_ux: 'Confusing UX — works but unclear',
  other: 'Other',
};

export const FEEDBACK_SEVERITY_LABELS: Record<FeedbackSeverity, string> = {
  blocker: 'Blocker — I can\'t do my job',
  high: 'High — significant daily friction',
  medium: 'Medium — annoying but workable',
  low: 'Low — nice to have',
};
